import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { mainApiUrl } from "../../globalVariables";
import "./Roadmap.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const COLLECTION      = "roadmapTasks";
const DEVS_DOC_PATH   = "roadmapConfig/devs"; // single doc holding the devs array

const STATUS_OPTIONS  = ["todo", "in_progress", "done", "blocked"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

const STATUS_LABELS = {
  todo:        "To Do",
  in_progress: "In Progress",
  done:        "Done",
  blocked:     "Blocked",
};

const PRIORITY_LABELS = {
  low:      "Low",
  medium:   "Medium",
  high:     "High",
  critical: "Critical",
};

const PRIORITY_RANK = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const ROADMAP_AI_ORG_ID = "WeWowNPgSR0R61eWniQN";

// Default Jira base URL — tasks with ticket IDs like "ADDY-1234" will link here
const JIRA_BASE_URL = "https://addyai.atlassian.net/browse/";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getISOWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

function getWeekLabel(isoWeek) {
  if (!isoWeek) return "";
  const [year, wPart] = isoWeek.split("-W");
  const weekNum = parseInt(wPart, 10);
  const jan4 = new Date(parseInt(year, 10), 0, 4);
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (weekNum - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

function getCurrentWeek() { return getISOWeek(new Date()); }

function getAdjacentWeek(isoWeek, delta) {
  const [year, wPart] = isoWeek.split("-W");
  const weekNum = parseInt(wPart, 10);
  const jan4 = new Date(parseInt(year, 10), 0, 4);
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (weekNum - 1) * 7 + delta * 7);
  return getISOWeek(monday);
}

function getMonthKey(date) {
  const d = date instanceof Date ? date : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtTs(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Build a Jira URL from a ticket string. Handles full URLs and bare IDs. */
function jiraUrl(ticket) {
  if (!ticket) return null;
  if (ticket.startsWith("http://") || ticket.startsWith("https://")) return ticket;
  return `${JIRA_BASE_URL}${ticket.trim()}`;
}

/** Return week dropdown options: prev-2, prev-1, current, next-1, next-2 */
function getWeekOptions() {
  const current = getCurrentWeek();
  return [
    { value: getAdjacentWeek(current, -2), label: `2 weeks ago  (${getWeekLabel(getAdjacentWeek(current, -2))})` },
    { value: getAdjacentWeek(current, -1), label: `Last week  (${getWeekLabel(getAdjacentWeek(current, -1))})` },
    { value: current,                       label: `This week  (${getWeekLabel(current)})` },
    { value: getAdjacentWeek(current,  1), label: `Next week  (${getWeekLabel(getAdjacentWeek(current, 1))})` },
    { value: getAdjacentWeek(current,  2), label: `In 2 weeks  (${getWeekLabel(getAdjacentWeek(current, 2))})` },
  ];
}

function newTaskDefaults(overrides = {}) {
  const week = getCurrentWeek();
  // Default placement is Backlog unless a specific week/placement is passed in
  return {
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    dueDate: "",
    assignee: "",
    week,
    month: getMonthKey(),
    isCurrentWeek: false,
    isNextWeek: false,
    isBacklog: true,
    jiraTicket: "",
    tags: [],
    ...overrides,
  };
}

function getTaskPriority(taskOrPriority) {
  const rawPriority = typeof taskOrPriority === "string"
    ? taskOrPriority
    : taskOrPriority?.priority;
  return PRIORITY_OPTIONS.includes(rawPriority) ? rawPriority : "medium";
}

function extractFieldValue(field) {
  if (!field) return "";
  if (typeof field === "string") return field.trim();
  if (typeof field?.value === "string") return field.value.trim();
  return "";
}

function toDateInputValue(value) {
  if (!value) return "";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDueDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getDueDateTimestamp(task) {
  if (!task?.dueDate) return Number.POSITIVE_INFINITY;
  const d = new Date(`${task.dueDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
  return d.getTime();
}

function inferPriorityFromText(text) {
  const lower = text.toLowerCase();
  if (/\b(critical|p0|urgent|asap|sev\s*1)\b/.test(lower)) return "critical";
  if (/\b(high|p1|important|top priority)\b/.test(lower)) return "high";
  if (/\b(low|p3|nice to have|whenever|eventually)\b/.test(lower)) return "low";
  return "medium";
}

function inferAssigneeFromText(text, devs = []) {
  const lower = text.toLowerCase();
  const matchedDev = devs.find((dev) => {
    const name = dev?.trim();
    if (!name) return false;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const mentionRegex = new RegExp(`(^|\\s)(@${escaped}|${escaped})(\\b|\\s|$)`, "i");
    return mentionRegex.test(lower);
  });
  return matchedDev || "";
}

function appendOriginalInputToDescription(existingDescription, aiDescription, originalInput) {
  const cleanOriginal = (originalInput || "").trim();
  if (!cleanOriginal) return existingDescription || aiDescription || "";

  const existing = (existingDescription || "").replace(/\n*Original input:\n[\s\S]*$/i, "").trim();
  const ai = (aiDescription || "").trim();
  const combinedTop = [ai, existing].filter(Boolean).join("\n\n");
  return [combinedTop, `Original input:\n${cleanOriginal}`].filter(Boolean).join("\n\n");
}

async function extractTaskDraftFromText({ text, currentForm, devs }) {
  const rawText = (text || "").trim();
  if (!rawText) return null;

  const fallback = {
    title: rawText.split("\n").map((line) => line.trim()).find(Boolean)?.slice(0, 120) || currentForm.title,
    description: appendOriginalInputToDescription(currentForm.description, "", rawText),
    assignee: inferAssigneeFromText(rawText, devs),
    priority: inferPriorityFromText(rawText),
  };

  try {
    const response = await fetch(
      `${mainApiUrl}/organization/${ROADMAP_AI_ORG_ID}/extract-task-fields`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationHistory: [{ role: "user", content: rawText }],
          currentFields: {
            taskTitle: currentForm.title || "",
            taskInstructions: currentForm.description || "",
          },
        }),
      },
    );

    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.success) return fallback;

    const extracted = payload?.data || {};
    const aiTitle =
      extractFieldValue(extracted.taskTitle) ||
      extractFieldValue(extracted.title) ||
      fallback.title;
    const aiDescription =
      extractFieldValue(extracted.taskInstructions) ||
      extractFieldValue(extracted.description) ||
      "";

    const aiAssigneeRaw =
      extractFieldValue(extracted.assignee) ||
      extractFieldValue(extracted.owner) ||
      "";
    const aiPriorityRaw =
      extractFieldValue(extracted.priority) ||
      inferPriorityFromText(rawText);

    const aiAssignee = devs.includes(aiAssigneeRaw) ? aiAssigneeRaw : fallback.assignee;
    const aiPriority = PRIORITY_OPTIONS.includes(aiPriorityRaw) ? aiPriorityRaw : fallback.priority;

    return {
      title: aiTitle || fallback.title,
      description: appendOriginalInputToDescription(currentForm.description, aiDescription, rawText),
      assignee: aiAssignee,
      priority: aiPriority,
    };
  } catch (error) {
    console.error("Roadmap AI extraction failed:", error);
    return fallback;
  }
}

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  return <span className={`rm-badge rm-status-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function PriorityBadge({ priority }) {
  const safePriority = getTaskPriority(priority);
  return <span className={`rm-badge rm-priority-${safePriority}`}>{PRIORITY_LABELS[safePriority]}</span>;
}

function Spinner() { return <span className="rm-spinner" />; }

function JiraLink({ ticket, className }) {
  if (!ticket) return null;
  const url = jiraUrl(ticket);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`rm-jira-link ${className || ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      🎫 {ticket}
    </a>
  );
}

// ─── Task Edit Modal ──────────────────────────────────────────────────────────

function TaskModal({ task, onSave, onClose, onDelete, devs }) {
  const currentWeek = getCurrentWeek();
  const weekOptions = getWeekOptions();

  const [form, setForm] = useState(() => {
    const w = task?.week || currentWeek;
    // Determine placement from week
    const isBacklog     = task?.isBacklog ?? false;
    const isCurrentWeek = !isBacklog && (task?.isCurrentWeek ?? (w === currentWeek));
    const isNextWeek    = !isBacklog && (task?.isNextWeek ?? (w === getAdjacentWeek(currentWeek, 1)));
    return {
      title:         task?.title || "",
      description:   task?.description || "",
      status:        task?.status || "todo",
      priority:      task?.priority || "medium",
      dueDate:       toDateInputValue(task?.dueDate),
      assignee:      task?.assignee || "",
      week:          w,
      month:         task?.month || getMonthKey(),
      isCurrentWeek,
      isNextWeek,
      isBacklog,
      jiraTicket:    task?.jiraTicket || "",
      tags:          task?.tags || [],
    };
  });

  const [saving, setSaving]           = useState(false);
  const [tagInput, setTagInput]       = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [aiText, setAiText]           = useState("");
  const [aiLoading, setAiLoading]     = useState(false);
  const [aiError, setAiError]         = useState("");
  const isNew = !task?.id;

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  // When week changes, auto-update the placement flags
  const handleWeekChange = (val) => {
    if (val === "__backlog__") {
      setForm((p) => ({ ...p, week: currentWeek, isBacklog: true, isCurrentWeek: false, isNextWeek: false }));
    } else {
      const isC = val === currentWeek;
      const isN = val === getAdjacentWeek(currentWeek, 1);
      setForm((p) => ({
        ...p,
        week: val,
        month: getMonthKey(),
        isBacklog: false,
        isCurrentWeek: isC,
        isNextWeek: isN,
      }));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave({ ...form, title: form.title.trim() });
    setSaving(false);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => set("tags", form.tags.filter((x) => x !== t));

  const applyDueDate = (value) => set("dueDate", value);

  const quickDates = (() => {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(now.getDate() + 1);
    const tomorrow = tomorrowDate.toISOString().slice(0, 10);
    const nextMondayDate = new Date(now);
    const day = now.getDay();
    const daysUntilMonday = day === 0 ? 1 : 8 - day;
    nextMondayDate.setDate(now.getDate() + daysUntilMonday);
    const nextMonday = nextMondayDate.toISOString().slice(0, 10);
    return { today, tomorrow, nextMonday };
  })();

  const handleAiAutofill = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    setAiError("");
    const extracted = await extractTaskDraftFromText({ text: aiText, currentForm: form, devs });
    if (!extracted) {
      setAiError("Could not parse this text. Please try with more detail.");
      setAiLoading(false);
      return;
    }
    setForm((prev) => ({
      ...prev,
      title: extracted.title || prev.title,
      description: extracted.description || prev.description,
      assignee: extracted.assignee || prev.assignee,
      priority: extracted.priority || prev.priority,
    }));
    setAiLoading(false);
  };

  // Current week value for the select
  const weekSelectValue = form.isBacklog ? "__backlog__" : (form.week || currentWeek);

  return (
    <div className="rm-modal-backdrop" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <span className="fw-bold">{isNew ? "New Task" : "Edit Task"}</span>
          <button className="rm-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="rm-modal-body">
          {/* Title */}
          <div className="rm-field">
            <label className="rm-label">Title *</label>
            <input
              className="rm-input"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Task title…"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
          </div>

          {/* Description */}
          <div className="rm-field">
            <label className="rm-label">Description</label>
            <textarea
              className="rm-input rm-textarea"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Optional description…"
              rows={2}
            />
          </div>

          {/* Status + Priority */}
          <div className="rm-field-row">
            <div className="rm-field">
              <label className="rm-label">Status</label>
              <select className="rm-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label className="rm-label">Priority</label>
              <select className="rm-select" value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div className="rm-field">
            <label className="rm-label">Due Date</label>
            <div className="rm-due-date-row">
              <input
                type="date"
                className="rm-input rm-date-input"
                value={form.dueDate || ""}
                onChange={(e) => applyDueDate(e.target.value)}
              />
              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => applyDueDate(quickDates.today)}>Today</button>
              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => applyDueDate(quickDates.tomorrow)}>Tomorrow</button>
              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => applyDueDate(quickDates.nextMonday)}>Next Monday</button>
              {form.dueDate && (
                <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => applyDueDate("")}>Clear</button>
              )}
            </div>
            <div className="rm-field-help">
              {form.dueDate ? `Selected: ${formatDueDate(form.dueDate)}` : "Optional. Use calendar or quick picks."}
            </div>
          </div>

          {/* Assignee + Week */}
          <div className="rm-field-row">
            <div className="rm-field">
              <label className="rm-label">Assignee</label>
              <select className="rm-select" value={form.assignee} onChange={(e) => set("assignee", e.target.value)}>
                <option value="">— Unassigned —</option>
                {devs.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label className="rm-label">Week / Placement</label>
              <select className="rm-select" value={weekSelectValue} onChange={(e) => handleWeekChange(e.target.value)}>
                {weekOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                <option value="__backlog__">📦 Backlog</option>
              </select>
            </div>
          </div>

          {/* Jira Ticket */}
          <div className="rm-field">
            <label className="rm-label">Jira Ticket</label>
            <div className="rm-jira-input-row">
              <input
                className="rm-input"
                value={form.jiraTicket}
                onChange={(e) => set("jiraTicket", e.target.value)}
                placeholder="e.g. ADDY-1234 or full URL"
              />
              {form.jiraTicket && (
                <a
                  href={jiraUrl(form.jiraTicket)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary rm-jira-open-btn"
                >
                  Open ↗
                </a>
              )}
            </div>
          </div>

          {/* AI Autofill */}
          <div className="rm-field">
            <label className="rm-label">AI Autofill from Text</label>
            <textarea
              className="rm-input rm-textarea"
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Paste a request, Jira text, or Slack message. AI will fill task fields and keep the raw original text in the description."
              rows={4}
            />
            <div className="d-flex align-items-center gap-2 mt-2">
              <button className="btn btn-sm btn-outline-primary" type="button" onClick={handleAiAutofill} disabled={aiLoading || !aiText.trim()}>
                {aiLoading ? <><Spinner /> Parsing…</> : "✨ Autofill from text"}
              </button>
              {aiError && <span className="rm-field-error mb-0">{aiError}</span>}
            </div>
          </div>

          {/* Tags */}
          <div className="rm-field">
            <label className="rm-label">Tags</label>
            <div className="rm-tags-row">
              {form.tags.map((t) => (
                <span key={t} className="rm-tag">
                  {t}
                  <button className="rm-tag-remove" onClick={() => removeTag(t)}>×</button>
                </span>
              ))}
              <input
                className="rm-input rm-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag…"
              />
              {tagInput && <button className="btn btn-sm btn-outline-secondary" onClick={addTag}>+</button>}
            </div>
          </div>

          {/* Week History */}
          {task?.weekHistory?.length > 0 && (
            <div className="rm-field">
              <label className="rm-label">Week History</label>
              <div className="rm-week-history">
                {task.weekHistory.map((entry, i) => (
                  <div key={i} className="rm-week-history-entry">
                    <span className="rm-week-history-from">
                      {entry.from === "backlog" ? "📦 Backlog" : entry.from}
                    </span>
                    <span className="rm-week-history-arrow">→</span>
                    <span className="rm-week-history-to">{entry.to}</span>
                    <span className="rm-week-history-date">
                      {new Date(entry.movedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    {entry.reason === "promoted" && (
                      <span className="rm-week-history-reason">promoted</span>
                    )}
                  </div>
                ))}
                <div className="rm-week-history-summary">
                  {task.weekHistory.length} move{task.weekHistory.length !== 1 ? "s" : ""} •{" "}
                  {(() => {
                    // Count unique weeks this task has been in
                    const weeks = new Set([
                      ...(task.weekHistory.map((e) => e.from).filter((w) => w !== "backlog")),
                      task.week,
                    ]);
                    return `${weeks.size} week${weeks.size !== 1 ? "s" : ""} total`;
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="rm-modal-footer">
          {!isNew && (
            confirmDelete ? (
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "0.8rem", color: "#dc3545" }}>Delete this task?</span>
                <button className="btn btn-sm btn-danger" onClick={() => onDelete(task.id)}>Yes, delete</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
            )
          )}
          <div className="ms-auto d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving || !form.title.trim()}>
              {saving ? <><Spinner /> Saving…</> : isNew ? "Create Task" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Quick Import Modal ───────────────────────────────────────────────────────

function QuickImportModal({ onImport, onClose, devs }) {
  const currentWeek = getCurrentWeek();
  const weekOptions = getWeekOptions();

  const [text, setText]           = useState("");
  const [status, setStatus]       = useState("todo");
  const [priority, setPriority]   = useState("medium");
  const [assignee, setAssignee]   = useState("");
  const [weekVal, setWeekVal]     = useState(currentWeek);
  const [isBacklog, setIsBacklog] = useState(false);
  const [importing, setImporting] = useState(false);

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);

  const handleWeekChange = (val) => {
    if (val === "__backlog__") { setIsBacklog(true); setWeekVal(currentWeek); }
    else { setIsBacklog(false); setWeekVal(val); }
  };

  const handleImport = async () => {
    if (!lines.length) return;
    setImporting(true);
    await onImport(lines.map((title) => ({
      title,
      description: "",
      status,
      priority,
      dueDate: "",
      assignee,
      week: isBacklog ? currentWeek : weekVal,
      month: getMonthKey(),
      isCurrentWeek: !isBacklog && weekVal === currentWeek,
      isNextWeek:    !isBacklog && weekVal === getAdjacentWeek(currentWeek, 1),
      isBacklog,
      jiraTicket: "",
      tags: [],
    })));
    setImporting(false);
    onClose();
  };

  return (
    <div className="rm-modal-backdrop" onClick={onClose}>
      <div className="rm-modal rm-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <span className="fw-bold">Quick Import Tasks</span>
          <button className="rm-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="rm-modal-body">
          <p className="text-muted mb-3" style={{ fontSize: "0.82rem" }}>
            Paste one task title per line. All tasks will be created with the settings below.
          </p>

          <div className="rm-field">
            <label className="rm-label">Task Titles (one per line)</label>
            <textarea
              className="rm-input rm-textarea rm-import-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"Fix login bug\nUpdate onboarding flow\nRefactor API bridge\n…"}
              rows={8}
              autoFocus
            />
            {lines.length > 0 && (
              <div className="rm-import-count">{lines.length} task{lines.length !== 1 ? "s" : ""} to import</div>
            )}
          </div>

          <div className="rm-field-row">
            <div className="rm-field">
              <label className="rm-label">Status</label>
              <select className="rm-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label className="rm-label">Priority</label>
              <select className="rm-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
            </div>
          </div>

          <div className="rm-field-row">
            <div className="rm-field">
              <label className="rm-label">Assignee</label>
              <select className="rm-select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">— Unassigned —</option>
                {devs.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label className="rm-label">Week / Placement</label>
              <select className="rm-select" value={isBacklog ? "__backlog__" : weekVal} onChange={(e) => handleWeekChange(e.target.value)}>
                {weekOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                <option value="__backlog__">📦 Backlog</option>
              </select>
            </div>
          </div>
        </div>

        <div className="rm-modal-footer">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm btn-primary ms-auto"
            onClick={handleImport}
            disabled={importing || lines.length === 0}
          >
            {importing ? <><Spinner /> Importing…</> : `Import ${lines.length} Task${lines.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Dev Manager Modal ────────────────────────────────────────────────────────

function DevManagerModal({ devs, onSave, onClose }) {
  const [list, setList] = useState([...devs]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const addDev = () => {
    const n = newName.trim();
    if (n && !list.includes(n)) setList((p) => [...p, n]);
    setNewName("");
  };

  const removeDev = (name) => setList((p) => p.filter((d) => d !== name));

  const moveDev = (idx, dir) => {
    const next = [...list];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setList(next);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(list);
    setSaving(false);
    onClose();
  };

  return (
    <div className="rm-modal-backdrop" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <span className="fw-bold">Manage Developers</span>
          <button className="rm-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="rm-modal-body">
          <p className="text-muted mb-3" style={{ fontSize: "0.82rem" }}>
            Add or remove devs. Order here determines order in dropdowns.
          </p>

          <div className="rm-dev-list">
            {list.length === 0 && (
              <div className="rm-empty-small">No devs yet. Add one below.</div>
            )}
            {list.map((name, idx) => (
              <div key={name} className="rm-dev-row">
                <div className="rm-dev-avatar">{name.charAt(0).toUpperCase()}</div>
                <span className="rm-dev-name">{name}</span>
                <div className="rm-dev-actions">
                  <button className="rm-icon-btn" onClick={() => moveDev(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                  <button className="rm-icon-btn" onClick={() => moveDev(idx, 1)} disabled={idx === list.length - 1} title="Move down">↓</button>
                  <button className="rm-icon-btn rm-icon-btn--danger" onClick={() => removeDev(name)} title="Remove">✕</button>
                </div>
              </div>
            ))}
          </div>

          <div className="rm-dev-add-row mt-3">
            <input
              className="rm-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addDev(); }}
              placeholder="Dev name…"
              autoFocus
            />
            <button className="btn btn-sm btn-primary" onClick={addDev} disabled={!newName.trim()}>Add</button>
          </div>
        </div>

        <div className="rm-modal-footer">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary ms-auto" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Task Card (used in This Week columns) ────────────────────────────────────

function TaskCard({ task, onClick, onDragStart, draggable }) {
  const moveCount = task.weekHistory?.length || 0;
  return (
    <div
      className={`rm-task-card ${draggable ? "rm-task-card--draggable" : ""}`}
      onClick={() => onClick(task)}
      draggable={!!draggable}
      onDragStart={draggable ? (e) => { e.dataTransfer.setData("taskId", task.id); onDragStart?.(task); } : undefined}
    >
      {draggable && <div className="rm-task-card-drag-handle" title="Drag to move">⠿</div>}
      <div className="rm-task-card-title">{task.title}</div>
      <div className="rm-task-card-meta">
        <StatusBadge status={task.status} />
        <PriorityBadge priority={task.priority} />
        {moveCount > 0 && (
          <span className="rm-task-card-moves" title={`Moved ${moveCount} time${moveCount !== 1 ? "s" : ""}`}>
            🔀 {moveCount}
          </span>
        )}
      </div>
      {task.assignee && <div className="rm-task-card-assignee">👤 {task.assignee}</div>}
      {task.dueDate && <div className="rm-task-card-assignee">📅 Due {formatDueDate(task.dueDate)}</div>}
      {task.jiraTicket && <JiraLink ticket={task.jiraTicket} className="rm-task-card-jira" />}
      {task.tags?.length > 0 && (
        <div className="rm-task-card-tags">
          {task.tags.map((t) => <span key={t} className="rm-tag">{t}</span>)}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard efficiency helpers ────────────────────────────────────────────

function buildEfficiencyData(tasks) {
  const currentWeek = getCurrentWeek();

  // Build a list of the last 6 weeks (oldest → newest)
  const weeks = [];
  for (let i = 5; i >= 0; i--) weeks.push(getAdjacentWeek(currentWeek, -i));

  const weekData = weeks.map((wk) => {
    const wkTasks = tasks.filter((t) => !t.isBacklog && t.week === wk);
    const done    = wkTasks.filter((t) => t.status === "done").length;
    const total   = wkTasks.length;

    // Carry-overs: tasks that have a weekHistory entry where `to === wk`
    // (they were moved INTO this week from a previous week)
    const carriedIn = tasks.filter((t) =>
      (t.weekHistory || []).some((h) => h.to === wk && h.from !== "__future__")
    ).length;

    // Tasks that were moved OUT of this week (to a later week)
    const movedOut = tasks.filter((t) =>
      (t.weekHistory || []).some((h) => h.from === wk)
    ).length;

    const completionRate = total ? Math.round((done / total) * 100) : 0;
    const carryOverRate  = total ? Math.round((movedOut / total) * 100) : 0;

    return { wk, label: wk === currentWeek ? "This week" : wk, total, done, carriedIn, movedOut, completionRate, carryOverRate };
  });

  // Tasks with most moves (top 5 carry-overs)
  const mostMoved = [...tasks]
    .filter((t) => (t.weekHistory?.length || 0) > 0)
    .sort((a, b) => (b.weekHistory?.length || 0) - (a.weekHistory?.length || 0))
    .slice(0, 5);

  // Average task age (weeks since creation) for non-done tasks
  const now = Date.now();
  const activeTasks = tasks.filter((t) => !t.isBacklog && t.status !== "done" && t.createdAt);
  const avgAgeWeeks = activeTasks.length
    ? (activeTasks.reduce((sum, t) => {
        const created = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
        return sum + (now - created.getTime()) / (7 * 24 * 60 * 60 * 1000);
      }, 0) / activeTasks.length).toFixed(1)
    : "—";

  // Overall carry-over rate across all weeks with data
  const weeksWithData = weekData.filter((w) => w.total > 0);
  const avgCarryOver  = weeksWithData.length
    ? Math.round(weeksWithData.reduce((s, w) => s + w.carryOverRate, 0) / weeksWithData.length)
    : 0;
  const avgCompletion = weeksWithData.length
    ? Math.round(weeksWithData.reduce((s, w) => s + w.completionRate, 0) / weeksWithData.length)
    : 0;

  return { weekData, mostMoved, avgAgeWeeks, avgCarryOver, avgCompletion, currentWeek };
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────

function DashboardTab({ tasks }) {
  const currentWeek = getCurrentWeek();
  const currentTasks = tasks.filter((t) => t.week === currentWeek && !t.isBacklog);
  const total        = tasks.filter((t) => !t.isBacklog).length;
  const inProgress   = tasks.filter((t) => t.status === "in_progress").length;
  const doneThisWeek = currentTasks.filter((t) => t.status === "done").length;
  const blocked      = tasks.filter((t) => t.status === "blocked").length;
  const backlog      = tasks.filter((t) => t.isBacklog).length;
  const progressPct  = currentTasks.length ? Math.round((doneThisWeek / currentTasks.length) * 100) : 0;

  const recent = [...tasks]
    .filter((t) => t.updatedAt)
    .sort((a, b) => {
      const ta = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(0);
      const tb = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(0);
      return tb - ta;
    })
    .slice(0, 8);

  const statusBreakdown   = STATUS_OPTIONS.map((s) => ({ status: s, count: tasks.filter((t) => !t.isBacklog && t.status === s).length }));
  const priorityBreakdown = PRIORITY_OPTIONS.map((p) => ({ priority: p, count: tasks.filter((t) => !t.isBacklog && t.priority === p).length }));

  const eff = buildEfficiencyData(tasks);
  const maxBarTotal = Math.max(...eff.weekData.map((w) => w.total), 1);

  return (
    <div className="rm-dashboard">
      {/* ── Stat cards ── */}
      <div className="rm-stat-row">
        {[
          { label: "Total Tasks",       value: total,               sub: "active" },
          { label: "In Progress",       value: inProgress,          sub: "tasks" },
          { label: "Done This Week",    value: doneThisWeek,        sub: "completed" },
          { label: "Blocked",           value: blocked,             sub: "need attention", alert: blocked > 0 },
          { label: "Backlog",           value: backlog,             sub: "items" },
          { label: "Avg Completion",    value: `${eff.avgCompletion}%`, sub: "last 6 weeks" },
          { label: "Avg Carry-over",    value: `${eff.avgCarryOver}%`,  sub: "tasks moved fwd", alert: eff.avgCarryOver > 40 },
          { label: "Avg Task Age",      value: eff.avgAgeWeeks,     sub: "weeks (active)" },
        ].map(({ label, value, sub, alert }) => (
          <div key={label} className={`rm-stat-card ${alert ? "rm-stat-card--alert" : ""}`}>
            <div className="rm-stat-label">{label}</div>
            <div className="rm-stat-value">{value}</div>
            <div className="rm-stat-sub">{sub}</div>
          </div>
        ))}
      </div>

      {/* ── Current week progress ── */}
      <div className="rm-panel mb-4">
        <div className="rm-panel-title">
          Current Week Progress
          <span className="rm-panel-sub ms-2">{getWeekLabel(currentWeek)}</span>
        </div>
        <div className="rm-week-progress-bar mt-2">
          <div className="rm-week-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="rm-week-progress-label">{doneThisWeek} / {currentTasks.length} tasks done ({progressPct}%)</div>
        {currentTasks.length > 0 && (
          <div className="rm-week-status-pills mt-2">
            {STATUS_OPTIONS.map((s) => {
              const count = currentTasks.filter((t) => t.status === s).length;
              return count ? <span key={s} className={`rm-badge rm-status-${s}`}>{STATUS_LABELS[s]}: {count}</span> : null;
            })}
          </div>
        )}
      </div>

      {/* ── Efficiency: week-over-week chart ── */}
      <div className="rm-panel mb-4">
        <div className="rm-panel-title">Week-over-Week Efficiency <span className="rm-panel-sub ms-1">(last 6 weeks)</span></div>
        <div className="rm-efficiency-chart mt-3">
          {eff.weekData.map((w) => (
            <div key={w.wk} className={`rm-eff-col ${w.wk === currentWeek ? "rm-eff-col--current" : ""}`}>
              {/* Stacked bar: done (green) + undone (grey) */}
              <div className="rm-eff-bar-wrap" title={`${w.done} done / ${w.total} total`}>
                <div className="rm-eff-bar-bg" style={{ height: `${(w.total / maxBarTotal) * 100}%` }}>
                  <div
                    className="rm-eff-bar-done"
                    style={{ height: w.total ? `${(w.done / w.total) * 100}%` : "0%" }}
                  />
                </div>
              </div>
              <div className="rm-eff-bar-numbers">
                <span className="rm-eff-done">{w.done}</span>
                {w.total > 0 && <span className="rm-eff-total">/{w.total}</span>}
              </div>
              {/* Carry-over indicator */}
              {w.movedOut > 0 && (
                <div className="rm-eff-moved-out" title={`${w.movedOut} task${w.movedOut !== 1 ? "s" : ""} moved to next week`}>
                  ↗ {w.movedOut}
                </div>
              )}
              {w.carriedIn > 0 && (
                <div className="rm-eff-carried-in" title={`${w.carriedIn} task${w.carriedIn !== 1 ? "s" : ""} carried in from previous`}>
                  ↘ {w.carriedIn}
                </div>
              )}
              <div className="rm-eff-label">{w.wk === currentWeek ? "Now" : w.wk.split("-W")[1] ? `W${w.wk.split("-W")[1]}` : w.wk}</div>
              {w.total > 0 && (
                <div className={`rm-eff-pct ${w.completionRate >= 80 ? "good" : w.completionRate >= 50 ? "ok" : "low"}`}>
                  {w.completionRate}%
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="rm-eff-legend mt-2">
          <span className="rm-eff-legend-item rm-eff-legend-done">■ Done</span>
          <span className="rm-eff-legend-item rm-eff-legend-total">■ Total</span>
          <span className="rm-eff-legend-item rm-eff-legend-out">↗ Moved to next week</span>
          <span className="rm-eff-legend-item rm-eff-legend-in">↘ Carried in</span>
        </div>
      </div>

      {/* ── Carry-over detail: tasks moved most ── */}
      {eff.mostMoved.length > 0 && (
        <div className="rm-panel mb-4">
          <div className="rm-panel-title">Most Moved Tasks <span className="rm-panel-sub ms-1">(highest carry-over count)</span></div>
          <div className="rm-moved-list mt-2">
            {eff.mostMoved.map((t) => {
              const moves = t.weekHistory?.length || 0;
              const created = t.createdAt?.toDate ? t.createdAt.toDate() : null;
              const ageWeeks = created ? ((Date.now() - created.getTime()) / (7 * 24 * 60 * 60 * 1000)).toFixed(1) : "?";
              return (
                <div key={t.id} className="rm-moved-item">
                  <div className="rm-moved-item-left">
                    <div className="rm-moved-item-title">{t.title}</div>
                    <div className="rm-moved-item-meta">
                      <StatusBadge status={t.status} />
                      {t.assignee && <span className="rm-badge rm-badge-assignee">👤 {t.assignee}</span>}
                      <span className="rm-badge" style={{ background: "#f1f3f5", color: "#495057" }}>
                        {ageWeeks} wks old
                      </span>
                    </div>
                  </div>
                  <div className="rm-moved-item-right">
                    <div className="rm-moved-count">{moves}</div>
                    <div className="rm-moved-label">move{moves !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Bottom panels ── */}
      <div className="rm-dashboard-bottom">
        <div className="rm-panel">
          <div className="rm-panel-title">Status Breakdown</div>
          <div className="d-flex flex-column gap-2 mt-2">
            {statusBreakdown.map(({ status, count }) => (
              <div key={status}>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.78rem" }}>
                  <span className="fw-medium">{STATUS_LABELS[status]}</span>
                  <span className="text-muted">{count}</span>
                </div>
                <div className="rm-breakdown-bar">
                  <div className={`rm-breakdown-fill rm-status-fill-${status}`} style={{ width: total ? `${(count / total) * 100}%` : "0%" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rm-panel">
          <div className="rm-panel-title">Priority Breakdown</div>
          <div className="d-flex flex-column gap-2 mt-2">
            {priorityBreakdown.map(({ priority, count }) => (
              <div key={priority}>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.78rem" }}>
                  <span className="fw-medium">{PRIORITY_LABELS[priority]}</span>
                  <span className="text-muted">{count}</span>
                </div>
                <div className="rm-breakdown-bar">
                  <div className={`rm-breakdown-fill rm-priority-fill-${priority}`} style={{ width: total ? `${(count / total) * 100}%` : "0%" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rm-panel">
          <div className="rm-panel-title">Recent Activity</div>
          {recent.length === 0 ? (
            <div className="rm-empty-small">No recent activity</div>
          ) : (
            <div className="rm-activity-list mt-2">
              {recent.map((t) => (
                <div key={t.id} className="rm-activity-item">
                  <div className="rm-activity-title">{t.title}</div>
                  <div className="rm-activity-meta">
                    <StatusBadge status={t.status} />
                    <span className="text-muted" style={{ fontSize: "0.72rem" }}>{fmtTs(t.updatedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── This Week Tab (with drag & drop) ────────────────────────────────────────

function ThisWeekTab({ tasks, onEdit, onAdd, readonly, onDrop }) {
  const currentWeek = getCurrentWeek();
  const prevWeek    = getAdjacentWeek(currentWeek, -1);
  const nextWeek    = getAdjacentWeek(currentWeek, 1);

  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggingId,  setDraggingId]  = useState(null);

  const columns = [
    { key: prevWeek,    label: "Previous Week", sub: getWeekLabel(prevWeek),    cls: "prev" },
    { key: currentWeek, label: "Current Week",  sub: getWeekLabel(currentWeek), cls: "current" },
    { key: nextWeek,    label: "Next Week",      sub: getWeekLabel(nextWeek),    cls: "next" },
  ];

  const getColTasks = (weekKey) => tasks.filter((t) => !t.isBacklog && t.week === weekKey);

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(colKey);
  };

  const handleDrop = (e, colKey) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("taskId");
    setDragOverCol(null);
    setDraggingId(null);
    if (taskId && onDrop) onDrop(taskId, colKey);
  };

  return (
    <div className="rm-week-columns">
      {columns.map(({ key, label, sub, cls }) => {
        const colTasks = getColTasks(key);
        const donePct  = colTasks.length ? Math.round((colTasks.filter((t) => t.status === "done").length / colTasks.length) * 100) : 0;
        const isOver   = dragOverCol === key;
        const isDragging = draggingId !== null;

        return (
          <div
            key={key}
            className={`rm-week-col rm-week-col--${cls} ${isOver && !readonly ? "rm-week-col--drop-target" : ""} ${isDragging && !readonly ? "rm-week-col--drag-active" : ""}`}
            onDragOver={!readonly ? (e) => handleDragOver(e, key) : undefined}
            onDragLeave={!readonly ? () => setDragOverCol(null) : undefined}
            onDrop={!readonly ? (e) => handleDrop(e, key) : undefined}
          >
            <div className="rm-week-col-header">
              <div>
                <div className="rm-week-col-title">{label}</div>
                <div className="rm-week-col-sub">{sub}</div>
              </div>
              <span className="rm-week-col-count">{colTasks.length}</span>
            </div>
            {colTasks.length > 0 && (
              <div className="rm-col-progress-bar mb-2">
                <div className="rm-col-progress-fill" style={{ width: `${donePct}%` }} />
              </div>
            )}
            <div className="rm-week-col-tasks">
              {colTasks.length === 0 ? (
                <div className={`rm-empty-col ${isOver && !readonly ? "rm-empty-col--over" : ""}`}>
                  {isOver && !readonly ? "Drop here" : "No tasks"}
                </div>
              ) : (
                colTasks.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    onClick={onEdit || (() => {})}
                    draggable={!readonly}
                    onDragStart={(task) => setDraggingId(task.id)}
                  />
                ))
              )}
            </div>
            {!readonly && onAdd && (
              <button
                className="rm-add-task-btn"
                onClick={() => onAdd({ week: key, isCurrentWeek: key === currentWeek, isNextWeek: key === nextWeek, isBacklog: false })}
              >
                + Add task
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Dev Workload Tab ─────────────────────────────────────────────────────────

function DevWorkloadTab({ tasks, devs, onEdit, onOpenDevManager, readonly }) {
  const activeTasks = tasks.filter((t) => !t.isBacklog);

  // In public/readonly mode, allow filtering to a single dev
  const allDevNames = [...new Set(activeTasks.map((t) => t.assignee?.trim()).filter(Boolean))].sort();
  const [selfFilter, setSelfFilter] = useState("all");

  const assigneeMap = {};
  activeTasks.forEach((t) => {
    const key = t.assignee?.trim() || "Unassigned";
    if (!assigneeMap[key]) assigneeMap[key] = [];
    assigneeMap[key].push(t);
  });

  // Show devs in defined order, then any extras from tasks
  const orderedDevs = [
    ...devs.filter((d) => assigneeMap[d]),
    ...Object.keys(assigneeMap).filter((k) => k !== "Unassigned" && !devs.includes(k)),
    ...(assigneeMap["Unassigned"] ? ["Unassigned"] : []),
  ].filter((d) => selfFilter === "all" || d === selfFilter);

  const maxCount = Math.max(...orderedDevs.map((a) => assigneeMap[a]?.length || 0), 1);

  if (orderedDevs.length === 0 && selfFilter === "all") {
    return (
      <div className="rm-empty-state">
        <div style={{ fontSize: "2.5rem" }}>👨‍💻</div>
        <div className="fw-semibold mt-2">No tasks assigned yet</div>
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>Add tasks with assignees to see workload.</div>
        {!readonly && onOpenDevManager && (
          <button className="btn btn-sm btn-outline-secondary mt-3" onClick={onOpenDevManager}>Manage Devs</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        {/* Self-filter: always available so devs can filter to themselves */}
        <div className="d-flex align-items-center gap-2">
          <label className="rm-label mb-0" style={{ whiteSpace: "nowrap" }}>Filter by:</label>
          <select
            className="rm-select"
            value={selfFilter}
            onChange={(e) => setSelfFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="all">All Devs</option>
            {allDevNames.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        {!readonly && onOpenDevManager && (
          <button className="btn btn-sm btn-outline-secondary" onClick={onOpenDevManager}>⚙ Manage Devs</button>
        )}
      </div>

      {orderedDevs.length === 0 ? (
        <div className="rm-empty-state">
          <div style={{ fontSize: "2rem" }}>🔍</div>
          <div className="fw-semibold mt-2">No tasks for this dev</div>
        </div>
      ) : (
        <div className="rm-workload">
          {orderedDevs.map((assignee) => {
            const devTasks = assigneeMap[assignee] || [];
            const statusCounts = {};
            STATUS_OPTIONS.forEach((s) => { statusCounts[s] = devTasks.filter((t) => t.status === s).length; });
            const barPct  = (devTasks.length / maxCount) * 100;
            const donePct = devTasks.length ? Math.round((statusCounts.done / devTasks.length) * 100) : 0;

            return (
              <div key={assignee} className="rm-workload-card">
                <div className="rm-workload-header">
                  <div className="rm-workload-avatar">
                    {assignee === "Unassigned" ? "?" : assignee.charAt(0).toUpperCase()}
                  </div>
                  <div className="rm-workload-info">
                    <div className="rm-workload-name">{assignee}</div>
                    <div className="rm-workload-count">{devTasks.length} task{devTasks.length !== 1 ? "s" : ""}</div>
                  </div>
                  <div className="rm-workload-done-pct">{donePct}% done</div>
                </div>
                <div className="rm-workload-bar-wrap">
                  <div className="rm-workload-bar" style={{ width: `${barPct}%` }} />
                </div>
                <div className="rm-workload-pills">
                  {STATUS_OPTIONS.map((s) => statusCounts[s] > 0 && (
                    <span key={s} className={`rm-badge rm-status-${s}`}>{STATUS_LABELS[s]}: {statusCounts[s]}</span>
                  ))}
                </div>
                <div className="rm-workload-tasks">
                  {devTasks.map((t) => (
                    <div
                      key={t.id}
                      className={`rm-workload-task-row ${onEdit ? "rm-workload-task-row--clickable" : ""}`}
                      onClick={() => onEdit && onEdit(t)}
                    >
                      <span className="rm-workload-task-title">{t.title}</span>
                      <div className="d-flex align-items-center gap-1">
                        <StatusBadge status={t.status} />
                        <PriorityBadge priority={t.priority} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── All Tasks Tab ────────────────────────────────────────────────────────────

function AllTasksTab({ tasks, onEdit, onAdd }) {
  const [sortKey, setSortKey]             = useState("updatedAt");
  const [sortDir, setSortDir]             = useState("desc");
  const [filterStatus, setFilterStatus]   = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [search, setSearch]               = useState("");

  const assignees = [...new Set(tasks.map((t) => t.assignee?.trim()).filter(Boolean))].sort();

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortIcon = (key) => sortKey !== key ? " ↕" : sortDir === "asc" ? " ↑" : " ↓";

  const filtered = tasks
    .filter((t) => filterStatus === "all" || t.status === filterStatus)
    .filter((t) => filterPriority === "all" || t.priority === filterPriority)
    .filter((t) => filterAssignee === "all" || (t.assignee?.trim() || "Unassigned") === filterAssignee)
    .filter((t) => !search || t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase()) ||
      t.jiraTicket?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortKey === "dueDate") {
        const tsA = getDueDateTimestamp(a);
        const tsB = getDueDateTimestamp(b);
        const hasA = Number.isFinite(tsA);
        const hasB = Number.isFinite(tsB);
        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;
        return sortDir === "asc" ? tsA - tsB : tsB - tsA;
      }

      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === "updatedAt" || sortKey === "createdAt") {
        va = va?.toDate ? va.toDate() : new Date(0);
        vb = vb?.toDate ? vb.toDate() : new Date(0);
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  return (
    <div className="rm-all-tasks">
      <div className="rm-toolbar">
        <input className="rm-input rm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" />
        <select className="rm-select rm-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select className="rm-select rm-filter-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
        </select>
        {assignees.length > 0 && (
          <select className="rm-select rm-filter-select" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
            <option value="all">All Assignees</option>
            {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <button className="btn btn-sm btn-primary ms-auto" onClick={() => onAdd({})}>+ New Task</button>
      </div>

      {filtered.length === 0 ? (
        <div className="rm-empty-state">
          <div style={{ fontSize: "2rem" }}>📋</div>
          <div className="fw-semibold mt-2">No tasks found</div>
        </div>
      ) : (
        <div className="rm-table-wrap">
          <table className="rm-table">
            <thead>
              <tr>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("title")}>Title{sortIcon("title")}</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("status")}>Status{sortIcon("status")}</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("priority")}>Priority{sortIcon("priority")}</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("assignee")}>Assignee{sortIcon("assignee")}</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("week")}>Week{sortIcon("week")}</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("dueDate")}>Due Date{sortIcon("dueDate")}</th>
                <th className="rm-th">Jira</th>
                <th className="rm-th rm-th-sortable" onClick={() => toggleSort("updatedAt")}>Updated{sortIcon("updatedAt")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="rm-tr" onClick={() => onEdit(t)}>
                  <td className="rm-td rm-td-title">
                    <span className="rm-table-title">{t.title}</span>
                    {t.isBacklog && <span className="rm-badge rm-badge-backlog ms-1">Backlog</span>}
                    {t.tags?.map((tag) => <span key={tag} className="rm-tag ms-1">{tag}</span>)}
                  </td>
                  <td className="rm-td"><StatusBadge status={t.status} /></td>
                  <td className="rm-td"><PriorityBadge priority={t.priority} /></td>
                  <td className="rm-td rm-td-assignee">{t.assignee || <span className="text-muted">—</span>}</td>
                  <td className="rm-td rm-td-week">{t.isBacklog ? <span className="rm-badge rm-badge-backlog">Backlog</span> : (t.week || <span className="text-muted">—</span>)}</td>
                  <td className="rm-td rm-td-week">{t.dueDate ? formatDueDate(t.dueDate) : <span className="text-muted">—</span>}</td>
                  <td className="rm-td rm-td-jira" onClick={(e) => e.stopPropagation()}>
                    {t.jiraTicket ? <JiraLink ticket={t.jiraTicket} /> : <span className="text-muted">—</span>}
                  </td>
                  <td className="rm-td rm-td-date">{fmtTs(t.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="rm-table-footer">{filtered.length} task{filtered.length !== 1 ? "s" : ""}</div>
    </div>
  );
}

// ─── Backlog Tab ──────────────────────────────────────────────────────────────

function BacklogTab({ tasks, onEdit, onAdd, onPromote }) {
  const backlogTasks = tasks.filter((t) => t.isBacklog);
  const [search, setSearch]               = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [prioritySort, setPrioritySort]   = useState("high_to_low");

  const filtered = backlogTasks
    .filter((t) => filterPriority === "all" || getTaskPriority(t) === filterPriority)
    .filter((t) => !search || t.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const rankA = PRIORITY_RANK[getTaskPriority(a)];
      const rankB = PRIORITY_RANK[getTaskPriority(b)];
      const priorityDiff = prioritySort === "high_to_low" ? rankB - rankA : rankA - rankB;
      if (priorityDiff !== 0) return priorityDiff;

      const dueA = getDueDateTimestamp(a);
      const dueB = getDueDateTimestamp(b);
      if (dueA !== dueB) return dueA - dueB;

      const timeA = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
      const timeB = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
      return timeB - timeA;
    });

  return (
    <div className="rm-backlog">
      <div className="rm-toolbar">
        <input className="rm-input rm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search backlog…" />
        <select className="rm-select rm-filter-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
        </select>
        <select className="rm-select rm-filter-select" value={prioritySort} onChange={(e) => setPrioritySort(e.target.value)}>
          <option value="high_to_low">Priority: High → Low</option>
          <option value="low_to_high">Priority: Low → High</option>
        </select>
        <button className="btn btn-sm btn-primary ms-auto" onClick={() => onAdd({ isBacklog: true, isCurrentWeek: false })}>
          + Add to Backlog
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="rm-empty-state">
          <div style={{ fontSize: "2.5rem" }}>📦</div>
          <div className="fw-semibold mt-2">Backlog is empty</div>
          <div className="text-muted" style={{ fontSize: "0.85rem" }}>Add items to the backlog to plan future work.</div>
          <button className="btn btn-primary btn-sm mt-3" onClick={() => onAdd({ isBacklog: true, isCurrentWeek: false })}>
            + Add to Backlog
          </button>
        </div>
      ) : (
        <div className="rm-backlog-list">
          {filtered.map((t) => (
            <div key={t.id} className="rm-backlog-item">
              <div className="rm-backlog-item-main" onClick={() => onEdit(t)}>
                <div className="rm-backlog-item-title">{t.title}</div>
                {t.description && <div className="rm-backlog-item-desc">{t.description}</div>}
                <div className="rm-backlog-item-meta">
                  <PriorityBadge priority={t.priority} />
                  {t.dueDate && <span className="rm-badge rm-badge-date">📅 {formatDueDate(t.dueDate)}</span>}
                  {t.assignee && <span className="rm-badge rm-badge-assignee">👤 {t.assignee}</span>}
                  {t.jiraTicket && <JiraLink ticket={t.jiraTicket} />}
                  {t.tags?.map((tag) => <span key={tag} className="rm-tag">{tag}</span>)}
                </div>
              </div>
              <div className="rm-backlog-item-actions">
                <button className="btn btn-sm btn-outline-primary" onClick={() => onPromote(t, "current")} title="Move to current week">→ This Week</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => onPromote(t, "next")} title="Move to next week">→ Next Week</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="rm-table-footer">{filtered.length} item{filtered.length !== 1 ? "s" : ""} in backlog</div>
    </div>
  );
}

// ─── Week Planner Tab ─────────────────────────────────────────────────────────

function WeekPlannerTab({ tasks, onPromote, onEdit }) {
  const currentWeek = getCurrentWeek();
  const nextWeek    = getAdjacentWeek(currentWeek, 1);
  const prevWeek    = getAdjacentWeek(currentWeek, -1);
  const prev2Week   = getAdjacentWeek(currentWeek, -2);

  const [targetWeek, setTargetWeek] = useState(currentWeek);
  const [sourceFilter, setSourceFilter] = useState("all"); // "backlog" | "prev" | "prev2" | "all"

  // Tasks already in the target week
  const targetTasks = tasks.filter((t) => !t.isBacklog && t.week === targetWeek);

  // Source pool: tasks NOT in target week, not done
  const sourceTasks = tasks.filter((t) => {
    if (t.week === targetWeek && !t.isBacklog) return false;
    if (t.status === "done") return false;
    if (sourceFilter === "backlog") return t.isBacklog;
    if (sourceFilter === "prev")    return !t.isBacklog && t.week === prevWeek;
    if (sourceFilter === "prev2")   return !t.isBacklog && t.week === prev2Week;
    // "all": backlog + previous weeks (not future)
    return t.isBacklog || (!t.isBacklog && t.week < targetWeek);
  });

  const targetLabel = targetWeek === currentWeek ? "This Week" : targetWeek === nextWeek ? "Next Week" : getWeekLabel(targetWeek);

  const donePct = targetTasks.length
    ? Math.round((targetTasks.filter((t) => t.status === "done").length / targetTasks.length) * 100)
    : 0;

  return (
    <div className="rm-planner">
      {/* Planner header */}
      <div className="rm-planner-header">
        <div>
          <div className="rm-planner-title">Week Planner</div>
          <div className="rm-planner-sub">Pull tasks from backlog or previous weeks into a target week.</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label className="rm-label mb-0" style={{ whiteSpace: "nowrap" }}>Planning for:</label>
          <select
            className="rm-select"
            value={targetWeek}
            onChange={(e) => setTargetWeek(e.target.value)}
          >
            <option value={currentWeek}>This Week ({getWeekLabel(currentWeek)})</option>
            <option value={nextWeek}>Next Week ({getWeekLabel(nextWeek)})</option>
          </select>
        </div>
      </div>

      <div className="rm-planner-body">
        {/* Left: source pool */}
        <div className="rm-planner-source">
          <div className="rm-planner-col-header">
            <div className="rm-planner-col-title">Available to Add</div>
            <div className="rm-planner-filter-tabs">
              {[
                { key: "all",    label: "All" },
                { key: "backlog",label: "Backlog" },
                { key: "prev",   label: "Last Week" },
                { key: "prev2",  label: "2 Weeks Ago" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`rm-planner-filter-tab ${sourceFilter === key ? "active" : ""}`}
                  onClick={() => setSourceFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {sourceTasks.length === 0 ? (
            <div className="rm-empty-col">Nothing to pull from this source</div>
          ) : (
            <div className="rm-planner-source-list">
              {sourceTasks.map((t) => (
                <div key={t.id} className="rm-planner-source-item">
                  <div className="rm-planner-source-item-main" onClick={() => onEdit(t)}>
                    <div className="rm-planner-item-title">{t.title}</div>
                    <div className="rm-planner-item-meta">
                      <StatusBadge status={t.status} />
                      <PriorityBadge priority={t.priority} />
                      {t.assignee && <span className="rm-badge rm-badge-assignee">👤 {t.assignee}</span>}
                      {t.isBacklog
                        ? <span className="rm-badge rm-badge-backlog">Backlog</span>
                        : <span className="rm-badge rm-badge-week">📅 {t.week}</span>
                      }
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary rm-planner-add-btn"
                    onClick={() => onPromote(t, targetWeek === currentWeek ? "current" : "next")}
                    title={`Move to ${targetLabel}`}
                  >
                    → {targetLabel}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: target week */}
        <div className="rm-planner-target">
          <div className="rm-planner-col-header">
            <div className="rm-planner-col-title">{targetLabel}</div>
            <span className="rm-week-col-count">{targetTasks.length}</span>
          </div>
          {targetTasks.length > 0 && (
            <>
              <div className="rm-col-progress-bar mb-1">
                <div className="rm-col-progress-fill" style={{ width: `${donePct}%` }} />
              </div>
              <div style={{ fontSize: "0.72rem", color: "#6c757d", marginBottom: "8px" }}>
                {targetTasks.filter((t) => t.status === "done").length}/{targetTasks.length} done
              </div>
            </>
          )}
          {targetTasks.length === 0 ? (
            <div className="rm-empty-col">No tasks yet — pull from the left panel</div>
          ) : (
            <div className="rm-planner-target-list">
              {targetTasks.map((t) => (
                <div key={t.id} className="rm-planner-target-item" onClick={() => onEdit(t)}>
                  <div className="rm-planner-item-title">{t.title}</div>
                  <div className="rm-planner-item-meta">
                    <StatusBadge status={t.status} />
                    {t.assignee && <span className="rm-badge rm-badge-assignee">👤 {t.assignee}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Root Page ────────────────────────────────────────────────────────────────

// ─── Task Read-only Modal (public view) ──────────────────────────────────────

function TaskReadonlyModal({ task, onClose }) {
  if (!task) return null;
  const moveCount = task.weekHistory?.length || 0;
  return (
    <div className="rm-modal-backdrop" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <span className="fw-bold">{task.title}</span>
          <button className="rm-icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="rm-modal-body">
          {/* Status + Priority */}
          <div className="rm-readonly-row">
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
          </div>

          {/* Description */}
          {task.description && (
            <div className="rm-readonly-field">
              <div className="rm-label">Description</div>
              <div className="rm-readonly-value">{task.description}</div>
            </div>
          )}

          {/* Meta row */}
          <div className="rm-readonly-meta">
            {task.assignee && (
              <div className="rm-readonly-field">
                <div className="rm-label">Assignee</div>
                <div className="rm-readonly-value">👤 {task.assignee}</div>
              </div>
            )}
            <div className="rm-readonly-field">
              <div className="rm-label">Week</div>
              <div className="rm-readonly-value">
                {task.isBacklog ? "📦 Backlog" : task.week}
              </div>
            </div>
            {task.dueDate && (
              <div className="rm-readonly-field">
                <div className="rm-label">Due Date</div>
                <div className="rm-readonly-value">📅 {formatDueDate(task.dueDate)}</div>
              </div>
            )}
            {task.jiraTicket && (
              <div className="rm-readonly-field">
                <div className="rm-label">Jira</div>
                <JiraLink ticket={task.jiraTicket} />
              </div>
            )}
          </div>

          {/* Tags */}
          {task.tags?.length > 0 && (
            <div className="rm-readonly-field">
              <div className="rm-label">Tags</div>
              <div className="rm-tags-row">
                {task.tags.map((t) => <span key={t} className="rm-tag">{t}</span>)}
              </div>
            </div>
          )}

          {/* Week history */}
          {moveCount > 0 && (
            <div className="rm-readonly-field">
              <div className="rm-label">Week History</div>
              <div className="rm-week-history">
                {task.weekHistory.map((entry, i) => (
                  <div key={i} className="rm-week-history-entry">
                    <span className="rm-week-history-from">
                      {entry.from === "backlog" ? "📦 Backlog" : entry.from}
                    </span>
                    <span className="rm-week-history-arrow">→</span>
                    <span className="rm-week-history-to">{entry.to}</span>
                    <span className="rm-week-history-date">
                      {new Date(entry.movedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                ))}
                <div className="rm-week-history-summary">
                  {moveCount} move{moveCount !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="rm-modal-footer">
          <div className="rm-readonly-notice">👁 View only — admin access required to edit</div>
          <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Unlock Modal ───────────────────────────────────────────────────────

function AdminUnlockModal({ onUnlock, onClose }) {
  const [pw, setPw]       = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = () => {
    if (pw === ADMIN_PASSWORD) {
      onUnlock();
      onClose();
    } else {
      setError(true);
      setPw("");
    }
  };

  return (
    <div className="rm-modal-backdrop" onClick={onClose}>
      <div className="rm-modal rm-modal--narrow" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <span className="fw-bold">🔒 Admin View</span>
          <button className="rm-icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="rm-modal-body">
          <p className="text-muted mb-3" style={{ fontSize: "0.82rem" }}>
            Enter the admin password to unlock editing and all tabs.
          </p>
          <div className="rm-field">
            <label className="rm-label">Password</label>
            <input
              className={`rm-input ${error ? "rm-input--error" : ""}`}
              type="password"
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="Enter password…"
              autoFocus
            />
            {error && <div className="rm-field-error">Incorrect password. Try again.</div>}
          </div>
        </div>
        <div className="rm-modal-footer">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary ms-auto" onClick={handleSubmit} disabled={!pw}>
            Unlock
          </button>
        </div>
      </div>
    </div>
  );
}

// Tabs visible to everyone (read-only in public mode)
const PUBLIC_TABS = [
  { key: "dashboard",    label: "Dashboard",    icon: "🏠" },
  { key: "this-week",    label: "This Week",    icon: "📅" },
  { key: "dev-workload", label: "Dev Workload", icon: "👨‍💻" },
];

// Extra tabs only visible in admin mode
const ADMIN_TABS = [
  { key: "week-planner", label: "Week Planner", icon: "🗓" },
  { key: "all-tasks",    label: "All Tasks",    icon: "📋" },
  { key: "backlog",      label: "Backlog",      icon: "📦" },
];

const ADMIN_PASSWORD = "batman";

export default function RoadmapPage() {
  const [activeTab, setActiveTab]     = useState("dashboard");
  const [tasks, setTasks]             = useState([]);
  const [devs, setDevs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalTask, setModalTask]     = useState(null);
  const [modalDefaults, setModalDefaults] = useState({});
  const [showImport, setShowImport]   = useState(false);
  const [showDevManager, setShowDevManager] = useState(false);
  const [isAdmin, setIsAdmin]         = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);

  // ── Load tasks + devs ──
  const loadAll = useCallback(async () => {
    try {
      const [taskSnap, devsSnap] = await Promise.all([
        getDocs(query(collection(firestoreProd, COLLECTION), orderBy("createdAt", "desc"))),
        getDocs(collection(firestoreProd, "roadmapConfig")),
      ]);
      setTasks(taskSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const devsDoc = devsSnap.docs.find((d) => d.id === "devs");
      setDevs(devsDoc?.data()?.list || []);
    } catch (err) {
      console.error("Failed to load roadmap data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Save devs ──
  const handleSaveDevs = async (list) => {
    await setDoc(doc(firestoreProd, "roadmapConfig", "devs"), { list });
    setDevs(list);
  };

  // ── Save task (create or update) ──
  const handleSave = async (formData) => {
    try {
      if (modalTask?.id) {
        const existing = tasks.find((t) => t.id === modalTask.id);
        const prevWeek = existing?.week;
        const newWeek  = formData.week;
        const weekChanged = prevWeek && newWeek && prevWeek !== newWeek;

        // Append a history entry if the week changed
        const existingHistory = existing?.weekHistory || [];
        const weekHistory = weekChanged
          ? [
              ...existingHistory,
              {
                from:    prevWeek,
                to:      newWeek,
                movedAt: new Date().toISOString(),
                reason:  "manual",
              },
            ]
          : existingHistory;

        const payload = { ...formData, weekHistory, updatedAt: serverTimestamp() };
        await updateDoc(doc(firestoreProd, COLLECTION, modalTask.id), payload);
        setTasks((prev) =>
          prev.map((t) =>
            t.id === modalTask.id ? { ...t, ...payload, updatedAt: { toDate: () => new Date() } } : t
          )
        );
      } else {
        const docRef = await addDoc(collection(firestoreProd, COLLECTION), {
          ...formData,
          weekHistory:  [],
          createdAt:    serverTimestamp(),
          updatedAt:    serverTimestamp(),
        });
        setTasks((prev) => [
          {
            id: docRef.id, ...formData, weekHistory: [],
            createdAt: { toDate: () => new Date() },
            updatedAt: { toDate: () => new Date() },
          },
          ...prev,
        ]);
      }
    } catch (err) {
      console.error("Failed to save task:", err);
    }
    setModalTask(null);
    setModalDefaults({});
  };

  // ── Bulk import ──
  const handleImport = async (taskList) => {
    try {
      const created = await Promise.all(
        taskList.map((t) =>
          addDoc(collection(firestoreProd, COLLECTION), { ...t, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
        )
      );
      const newTasks = taskList.map((t, i) => ({
        id: created[i].id, ...t,
        createdAt: { toDate: () => new Date() },
        updatedAt: { toDate: () => new Date() },
      }));
      setTasks((prev) => [...newTasks, ...prev]);
    } catch (err) {
      console.error("Failed to import tasks:", err);
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(firestoreProd, COLLECTION, id));
      setTasks((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error("Failed to delete task:", err);
    }
    setModalTask(null);
    setModalDefaults({});
  };

  const handleAdd  = (defaults = {}) => { setModalDefaults(defaults); setModalTask({}); };
  const handleEdit = (task) => { setModalTask(task); setModalDefaults({}); };

  // ── Drag & drop: move task to a different week column ──
  const handleWeekDrop = useCallback(async (taskId, toWeek) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.week === toWeek) return;

    const currentWeek = getCurrentWeek();
    const nextWeek    = getAdjacentWeek(currentWeek, 1);
    const fromWeek    = task.isBacklog ? "backlog" : (task.week || "backlog");

    const weekHistory = [
      ...(task.weekHistory || []),
      { from: fromWeek, to: toWeek, movedAt: new Date().toISOString(), reason: "drag" },
    ];

    const updates = {
      week:          toWeek,
      month:         getMonthKey(),
      isBacklog:     false,
      isCurrentWeek: toWeek === currentWeek,
      isNextWeek:    toWeek === nextWeek,
      weekHistory,
      updatedAt:     serverTimestamp(),
    };
    try {
      await updateDoc(doc(firestoreProd, COLLECTION, taskId), updates);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, ...updates, updatedAt: { toDate: () => new Date() } } : t
        )
      );
    } catch (err) {
      console.error("Failed to move task:", err);
    }
  }, [tasks]);

  // ── Promote backlog/old-week item to a week ──
  const handlePromote = async (task, target) => {
    const currentWeek = getCurrentWeek();
    const nextWeek    = getAdjacentWeek(currentWeek, 1);
    const toWeek      = target === "current" ? currentWeek : nextWeek;
    const fromWeek    = task.isBacklog ? "backlog" : (task.week || "backlog");

    const existingHistory = task.weekHistory || [];
    const weekHistory = [
      ...existingHistory,
      {
        from:    fromWeek,
        to:      toWeek,
        movedAt: new Date().toISOString(),
        reason:  "promoted",
      },
    ];

    const updates = {
      isBacklog:     false,
      isCurrentWeek: target === "current",
      isNextWeek:    target === "next",
      week:          toWeek,
      month:         getMonthKey(),
      weekHistory,
      updatedAt:     serverTimestamp(),
    };
    try {
      await updateDoc(doc(firestoreProd, COLLECTION, task.id), updates);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, ...updates, updatedAt: { toDate: () => new Date() } } : t
        )
      );
    } catch (err) {
      console.error("Failed to promote task:", err);
    }
  };

  const backlogCount = tasks.filter((t) => t.isBacklog).length;
  const blockedCount = tasks.filter((t) => t.status === "blocked").length;

  // In public mode, clicking a task opens it read-only (no edit handler)
  const handleEditOrView = isAdmin ? handleEdit : null;

  const visibleTabs = isAdmin ? [...PUBLIC_TABS, ...ADMIN_TABS] : PUBLIC_TABS;

  // If current tab becomes hidden on lock, reset to dashboard
  const handleLock = () => {
    setIsAdmin(false);
    if (!PUBLIC_TABS.find((t) => t.key === activeTab)) setActiveTab("dashboard");
  };

  return (
    <div className="RoadmapContainer">
      <div className="container-fluid py-4 px-4">
        {/* Page header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="mb-0 fw-bold">Roadmap</h4>
            <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
              Company roadmap — tasks, progress, and dev workload at a glance.
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {!isAdmin ? (
              <>
                <span className="rm-readonly-badge">👁 View only</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdminModal(true)}>
                  🔒 Admin View
                </button>
              </>
            ) : (
              <>
                <span className="rm-admin-badge">🔓 Admin</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowImport(true)}>⬇ Import</button>
                <button className="btn btn-sm btn-primary" onClick={() => handleAdd({})}>+ New Task</button>
                <button className="btn btn-sm btn-outline-danger" onClick={handleLock} title="Exit admin mode">🔒 Lock</button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="rm-tabs">
          {visibleTabs.map(({ key, label, icon }) => {
            let badge = null;
            if (key === "backlog" && backlogCount > 0) badge = backlogCount;
            if (key === "this-week" && blockedCount > 0) badge = `${blockedCount} blocked`;
            return (
              <button
                key={key}
                className={`rm-tab ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <span className="rm-tab-icon">{icon}</span>
                <span>{label}</span>
                {badge !== null && <span className="rm-tab-badge">{badge}</span>}
              </button>
            );
          })}
          {/* Admin-only tabs teaser when locked */}
          {!isAdmin && (
            <button
              className="rm-tab rm-tab--locked"
              onClick={() => setShowAdminModal(true)}
              title="Unlock admin view to access more tabs"
            >
              <span className="rm-tab-icon">🔒</span>
              <span>More…</span>
            </button>
          )}
        </div>

        {/* Tab content */}
        <div className="rm-tab-content">
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted py-5 justify-content-center">
              <Spinner /> Loading roadmap…
            </div>
          ) : (
            <>
              {activeTab === "dashboard"    && <DashboardTab tasks={tasks} />}
              {activeTab === "this-week"    && (
                <ThisWeekTab
                  tasks={tasks}
                  onEdit={handleEditOrView}
                  onAdd={isAdmin ? handleAdd : null}
                  onDrop={isAdmin ? handleWeekDrop : null}
                  readonly={!isAdmin}
                />
              )}
              {activeTab === "week-planner" && isAdmin && (
                <WeekPlannerTab tasks={tasks} onPromote={handlePromote} onEdit={handleEdit} />
              )}
              {activeTab === "dev-workload" && (
                <DevWorkloadTab
                  tasks={tasks}
                  devs={devs}
                  onEdit={handleEditOrView}
                  onOpenDevManager={isAdmin ? () => setShowDevManager(true) : null}
                  readonly={!isAdmin}
                />
              )}
              {activeTab === "all-tasks"    && isAdmin && (
                <AllTasksTab tasks={tasks} onEdit={handleEdit} onAdd={handleAdd} />
              )}
              {activeTab === "backlog"      && isAdmin && (
                <BacklogTab tasks={tasks} onEdit={handleEdit} onAdd={handleAdd} onPromote={handlePromote} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Admin unlock modal */}
      {showAdminModal && (
        <AdminUnlockModal
          onUnlock={() => setIsAdmin(true)}
          onClose={() => setShowAdminModal(false)}
        />
      )}

      {/* Task modal — admin only */}
      {isAdmin && modalTask !== null && (
        <TaskModal
          task={modalTask?.id ? modalTask : { ...newTaskDefaults(modalDefaults) }}
          onSave={handleSave}
          onClose={() => { setModalTask(null); setModalDefaults({}); }}
          onDelete={handleDelete}
          devs={devs}
        />
      )}

      {/* Read-only task detail — public mode */}
      {!isAdmin && modalTask !== null && (
        <TaskReadonlyModal
          task={modalTask}
          onClose={() => setModalTask(null)}
        />
      )}

      {/* Quick import modal */}
      {isAdmin && showImport && (
        <QuickImportModal
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          devs={devs}
        />
      )}

      {/* Dev manager modal */}
      {isAdmin && showDevManager && (
        <DevManagerModal
          devs={devs}
          onSave={handleSaveDevs}
          onClose={() => setShowDevManager(false)}
        />
      )}
    </div>
  );
}
